import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { BannerComponent } from '@components';
import { TranslateService } from '@ngx-translate/core';
import {
  DashboardSelectedTab,
  DashboardStore,
  DashboardStoreStatusType,
  DashboardTileType,
} from '@store';
import { ViLoaderComponent } from '../../../../../projects/ui/components/loader/loader.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonStandaloneModule } from '../../../../shared/common-standalone.module';
import { BannerType } from '../../../../shared/components/banner/banner.model';
import { DashboardService } from '../../dashboard.service';
import { DashboardInstallationsTileTranslations } from '@vi-iot/client/index';

@Component({
  selector: 'app-dashboard-tile-banners',
  templateUrl: './dashboard-tile-banners.component.html',
  styleUrls: ['./dashboard-tile-banners.component.scss'],
  standalone: true,
  imports: [
    forwardRef(() => CommonStandaloneModule),
    ViLoaderComponent,
    BannerComponent,
    MatButtonModule,
  ],
})
export class DashboardTileBannersComponent implements OnInit, OnDestroy {
  @Input() dashboardStoreStatus: DashboardStoreStatusType;
  @Input() tileType: DashboardTileType;
  readonly dashboardStoreStatusTypes = DashboardStoreStatusType;
  protected readonly dashboardTileType = DashboardTileType;
  protected readonly bannerType = BannerType;
  activeTileTab: DashboardSelectedTab;
  emptyTileDataText: string;
  emptyTabDataText: string;
  errorBannerText: string;
  errorBannerBannerButtonLabel: string;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  private tabTranslations: DashboardInstallationsTileTranslations;

  constructor(
    private dashboardService: DashboardService,
    private dashboardStore: DashboardStore,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.tabTranslations = this.dashboardService.dashboardTileTypeTranslations(this.tileType);

    if (this.dashboardService.dashboardTileHasTabs(this.tileType)) {
      this.dashboardService
        .getActiveTabForTile(this.tileType)
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((activeTab: DashboardSelectedTab) => {
          this.activeTileTab = activeTab;
          this.emptyTabDataText =
            this.tabTranslations.tabs[this.activeTileTab.selectedTabValue]?.tabEmptyData ??
            this.translateService.instant('+DASHBOARD.DASHBOARD_TILES.EMPTY_DATA');
        });
    }

    this.emptyTileDataText =
      this.tabTranslations.tileEmptyData ??
      this.translateService.instant('+DASHBOARD.DASHBOARD_TILES.EMPTY_DATA');

    this.errorBannerText = this.translateService.instant(
      '+DASHBOARD.DASHBOARD_TILES.CONTENT_COULD_NOT_BE_LOADED'
    );
    this.errorBannerBannerButtonLabel = this.translateService.instant(
      '+DASHBOARD.DASHBOARD_TILES.RELOAD'
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  onReloadClick(): void {
    if (this.tileType === DashboardTileType.ContractsEndingSoon) {
      this.dashboardStore.initContractsEndingSoon(100);
      this.dashboardStore.getInstallationCounterForTiles([this.tileType]);
    } else if (
      this.dashboardService.dashboardTileHasTabs(this.tileType) &&
      this.dashboardStoreStatus === this.dashboardStoreStatusTypes.ErrorTab
    ) {
      this.dashboardStore.initInstallationsForSingleTileType(
        this.tileType,
        this.activeTileTab.selectedTabValue
      );
      this.dashboardStore.getInstallationCounterForTileTab(
        this.tileType,
        this.activeTileTab.selectedTabValue
      );
    } else {
      this.dashboardStore.initInstallationsForMultipleTileTypes([this.tileType]);
      this.dashboardStore.getInstallationCounterForTiles([this.tileType]);
    }
  }
}
